import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import {
  getPaymentHistoryGroups,
  getPaymentHistoryByGroupId,
  downloadGroupInvoicePdfs,
  markPaymentAsNotPaid,
  markPaymentAsReturn,
} from "@/data/requests/invoice-requests";
import { SearchView } from "@/components";
import ListView from "@/components/list-view";
import { countryOptions } from "@/utils/country-options";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "notPaid", label: "Not Paid" },
  { value: "return", label: "Return" },
];

export function PaymentHistoryListScreen() {
  const { toast } = useToast();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [isGroupDetailsOpen, setIsGroupDetailsOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useState({
    country: "",
  });

  const { data: paymentHistoryGroups, isLoading: isLoadingPaymentHistory } =
    useQuery({
      queryKey: ["paymentHistoryGroups", page, pageSize, searchParams],
      queryFn: () =>
        getPaymentHistoryGroups({
          page,
          pageSize,
          countryFilter: searchParams.country,
        }),
      onError: (error) => {
        toast({
          title: "Error loading payment history",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          variant: "destructive",
        });
      },
    });

  const {
    data: groupDetails,
    isLoading: isLoadingGroupDetails,
    refetch: refetchGroupDetails,
  } = useQuery({
    queryKey: ["paymentHistoryGroup", selectedGroupId],
    queryFn: () => getPaymentHistoryByGroupId(selectedGroupId),
    enabled: !!selectedGroupId && isGroupDetailsOpen,
    onError: (error) => {
      toast({
        title: "Error loading group details",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const handleViewGroupDetails = (groupId) => {
    setSelectedGroupId(groupId);
    setIsGroupDetailsOpen(true);
  };

  const handleDownloadGroupPdfs = (groupId) => {
    downloadGroupInvoicePdfs(groupId)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoices-${groupId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Download successful",
          description: "Invoice PDFs have been downloaded as a ZIP file.",
          variant: "default",
        });
      })
      .catch((error) => {
        toast({
          title: "Error downloading PDFs",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          variant: "destructive",
        });
        console.error("Error downloading PDFs:", error);
      });
  };

  const handleMarkAsNotPaid = (paymentHistoryId) => {
    markPaymentAsNotPaid(paymentHistoryId)
      .then(() => {
        toast({
          title: "Success",
          description: "Payment has been marked as not paid",
          variant: "default",
        });
        // Refresh the group details
        if (selectedGroupId) {
          refetchGroupDetails();
        }
      })
      .catch((error) => {
        toast({
          title: "Error",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          variant: "destructive",
        });
      });
  };

  const handleReturnClick = (paymentHistoryId) => {
    setSelectedPaymentId(paymentHistoryId);
    setIsReturnDialogOpen(true);
  };

  const handleReturnSubmit = () => {
    if (!returnReason.trim()) {
      toast({
        title: "Error",
        description: "Please enter a return reason",
        variant: "destructive",
      });
      return;
    }

    markPaymentAsReturn(selectedPaymentId, returnReason)
      .then(() => {
        toast({
          title: "Success",
          description: "Payment has been marked as returned",
          variant: "default",
        });
        setIsReturnDialogOpen(false);
        setReturnReason("");
        if (selectedGroupId) {
          refetchGroupDetails();
        }
      })
      .catch((error) => {
        toast({
          title: "Error",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          variant: "destructive",
        });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);
    setPage(1);
  };

  const pageSettings = {
    title: "Payment History",
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      accessor: (item) => formatDate(item.createdAt),
    },
    {
      key: "paymentDate",
      label: "Payment Date",
      accessor: (item) => item.paymentDate || "-",
    },
    {
      key: "recordCount",
      label: "Records",
      accessor: (item) => item.recordCount,
    },
    {
      key: "totalAmount",
      label: "Total Amount",
      accessor: (item) => item.totalAmount.toFixed(2),
    },
    {
      key: "currencies",
      label: "Currencies",
      accessor: (item) => item.currencies.join(", ") || "-",
    },
    {
      key: "country",
      label: "Country",
      accessor: (item) => item.country || "-",
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewGroupDetails(row.groupId)}
          >
            View Details
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => handleDownloadGroupPdfs(row.groupId)}
          >
            Download PDFs
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      loading={isLoadingPaymentHistory}
      style={{ padding: "0 10px" }}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams({
            date: "",
            status: "",
            country: "",
          });
          setPage(1);
        }}
      />

      <ListView
        isLoading={isLoadingPaymentHistory}
        data={paymentHistoryGroups?.data}
        columns={columns}
        totalItems={paymentHistoryGroups?.totalItems}
        enablePagination={true}
        itemsPerPage={pageSize}
        currentPage={page}
        onPageChange={setPage}
        renderGridItem={(item) => (
          <div className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium">
                    Date: {formatDate(item.createdAt)}
                  </p>
                  <p className="text-sm">
                    Payment Date: {item.paymentDate || "-"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    Records: {item.recordCount}
                  </p>
                  <p className="text-sm">
                    Amount: {item.totalAmount.toFixed(2)}
                  </p>
                </div>
              </div>
              <p className="text-sm">
                Currencies: {item.currencies.join(", ") || "-"}
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleViewGroupDetails(item.groupId)}
                >
                  View Details
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDownloadGroupPdfs(item.groupId)}
                >
                  Download PDFs
                </Button>
              </div>
            </div>
          </div>
        )}
      />

      {/* Group Details Dialog */}
      <Dialog open={isGroupDetailsOpen} onOpenChange={setIsGroupDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Group Details</DialogTitle>
            <DialogDescription>
              Details for payment group {selectedGroupId}
            </DialogDescription>
          </DialogHeader>

          {isLoadingGroupDetails ? (
            <div className="flex justify-center p-4">
              Loading group details...
            </div>
          ) : groupDetails?.data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-md">
                <div>
                  <p className="text-sm font-medium">Group ID:</p>
                  <p className="text-sm">{groupDetails.data.groupId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Payment Date:</p>
                  <p className="text-sm">
                    {groupDetails.data.paymentDate || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Amount:</p>
                  <p className="text-sm">
                    {groupDetails.data.totalAmount.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Currencies:</p>
                  <p className="text-sm">
                    {groupDetails.data.currencies.join(", ") || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Record Count:</p>
                  <p className="text-sm">{groupDetails.data.recordCount}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Created At:</p>
                  <p className="text-sm">
                    {formatDate(groupDetails.data.batchCreatedAt)}
                  </p>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={() =>
                  handleDownloadGroupPdfs(groupDetails.data.groupId)
                }
              >
                Download All PDFs
              </Button>

              <Table>
                <TableCaption>Payment records in this group</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice ID</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Bank Name</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupDetails.data.paymentHistories.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.commissionDetail?.invoiceId || "-"}
                      </TableCell>
                      <TableCell>
                        {payment.commissionDetail?.commission?.Client?.name ||
                          "-"}
                      </TableCell>
                      <TableCell>{payment.amount.toFixed(2)}</TableCell>
                      <TableCell>{payment.currency || "-"}</TableCell>
                      <TableCell>{payment.bankName || "-"}</TableCell>
                      <TableCell>{payment.recipientName || "-"}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleMarkAsNotPaid(payment.id)}
                          >
                            Not Paid
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReturnClick(payment.id)}
                          >
                            Return
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center p-4">No data found for this group</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return Reason Dialog */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Payment</DialogTitle>
            <DialogDescription>
              Please provide a reason for returning this payment.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="returnReason">Return Reason</Label>
              <Input
                id="returnReason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Enter return reason..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReturnDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleReturnSubmit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
